import { useEffect, useState } from "react";
import { api, formatApiError, formatIDR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Trash2, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function TyreCatalogPage() {
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [editRow, setEditRow] = useState(null); // {id, brand, size, price}
  const [form, setForm] = useState({ brand: "", size: "", price: 3000000 });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tyre-catalog").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submitNew = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tyre-catalog", { brand: form.brand.trim(), size: form.size.trim(), price: Number(form.price) });
      toast.success("Entry ditambahkan ke master");
      setOpenNew(false);
      setForm({ brand: "", size: "", price: 3000000 });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/tyre-catalog/${editRow.id}`, { price: Number(editRow.price) });
      toast.success("Harga diperbarui");
      setEditRow(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const del = async (row) => {
    if (!confirm(`Hapus ${row.brand} · ${row.size} dari master?`)) return;
    try {
      await api.delete(`/tyre-catalog/${row.id}`);
      toast.success("Entry dihapus");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">Master Harga Ban</h1>
          <p className="text-slate-500 text-sm mt-1">
            Kombinasi <b>Merk + Ukuran + Harga</b>. Koordinator hanya bisa pilih dari daftar ini saat input ban baru — harga tidak terlihat oleh mereka.
          </p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button
              className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase tracking-wider rounded-sm btn-industrial h-12"
              data-testid="add-catalog-btn"
            >
              <Plus className="w-5 h-5 mr-1" /> Tambah Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm border-2 border-slate-900">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black">Tambah Master Harga</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitNew} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Merk *</Label>
                  <Input data-testid="catalog-brand" required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="h-12 rounded-sm" placeholder="Bridgestone" />
                </div>
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Ukuran *</Label>
                  <Input data-testid="catalog-size" required value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="h-12 rounded-sm font-mono" placeholder="11R22.5" />
                </div>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Harga (Rp) *</Label>
                <Input data-testid="catalog-price" required type="number" min={0} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-12 rounded-sm font-mono text-lg" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} data-testid="catalog-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                  {busy ? "Menyimpan..." : "Simpan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border-2 border-slate-200 bg-white rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-900 hover:bg-slate-900">
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Merk</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Ukuran</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Harga</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold w-32 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`catalog-row-${r.brand}-${r.size}`}>
                <TableCell className="font-bold">{r.brand}</TableCell>
                <TableCell className="font-mono">{r.size}</TableCell>
                <TableCell className="font-mono">{formatIDR(r.price)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditRow({ ...r })} data-testid={`edit-${r.brand}-${r.size}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r)} data-testid={`delete-${r.brand}-${r.size}`}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-12 text-slate-500">
                  <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Belum ada entry master.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="rounded-sm border-2 border-slate-900">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">Ubah Harga</DialogTitle>
          </DialogHeader>
          {editRow && (
            <form onSubmit={submitEdit} className="space-y-4">
              <div className="border-2 border-slate-900 p-3 rounded-sm bg-slate-50">
                <div className="font-mono font-bold text-lg">{editRow.brand} · {editRow.size}</div>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Harga Baru (Rp) *</Label>
                <Input required type="number" min={0} value={editRow.price} onChange={(e) => setEditRow({ ...editRow, price: e.target.value })} className="h-12 rounded-sm font-mono text-lg" data-testid="edit-price-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditRow(null)} className="rounded-sm">Batal</Button>
                <Button type="submit" disabled={busy} data-testid="edit-price-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                  {busy ? "..." : "Simpan"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
